<template>
	<nav
		v-if="breadcrumbs.length"
		aria-label="breadcrumb"
	>
		<UiHorizontalSlider
			#default="{scrollToView}"
			:active-slug="String(items?.[items.length - 1]?.slug)"
			:show-cover="!hideBg"
		>
			<ul class="whitespace-nowrap overflow-x-auto w-full typo-body-s py-0.5">
				<li class="inline mr-1.5 md:mr-2 align-text-bottom" @click="(event) => scrollBreadcrumbIntoView(event, scrollToView)">
					<AbsoluteInternalLink to="/">
						<span class="underline hover:no-underline">Úvod</span>
					</AbsoluteInternalLink>
				</li>
				<li
					v-for="(item, index) in breadcrumbs"
					:key="item.id"
					class="inline mr-1.5 md:mr-2 align-text-bottom"
					:aria-current="index + 1 === breadcrumbs.length ? 'page' : undefined"
					:data-slug="item.slug"
					@click="(event) => scrollBreadcrumbIntoView(event, scrollToView)"
				>
					<NuxtIcon
						name="chevron-right"
						class="text-sm sm:text-xl mr-1.5 md:mr-2 inline-block translate-y-1"
						aria-hidden="true"
						filled
					/>
					<span v-if="index + 1 === breadcrumbs.length || !item.slug" class="font-bold">
						{{ item.name }}
					</span>
					<AbsoluteInternalLink
						v-else
						:to="breadcrumbLink(item.slug)"
						:class="{'underline hover:no-underline': item.slug}"
					>
						{{ item.name }}
					</AbsoluteInternalLink>
				</li>
			</ul>
		</UiHorizontalSlider>
	</nav>
</template>

<script setup lang="ts">
	import type {DeepReadonly, MaybeRef} from 'vue';
	import type {RouteLocationRaw} from 'vue-router';
	import {gsap} from 'gsap';
	import {ScrollToPlugin} from 'gsap/ScrollToPlugin';
	import {ensureLeadingSlash} from '~/utils';
	import type {BreadcrumbItem} from '~/types/breadcrumbs';

	if (import.meta.client) {
		gsap.registerPlugin(ScrollToPlugin);
	}

	type BreadcrumbListItem = BreadcrumbItem | DeepReadonly<BreadcrumbItem>;

	interface Props {
		items?: ReadonlyArray<BreadcrumbListItem>;
		hideBg?: boolean;
	}

	const props = defineProps<Props>();
	const injectedItems = inject<MaybeRef<ReadonlyArray<BreadcrumbListItem>>>('breadcrumbs', []);
	const breadcrumbs = computed(() => props.items ?? unref(injectedItems));

	function breadcrumbLink(slug: BreadcrumbListItem['slug']): RouteLocationRaw {
		if (typeof slug === 'string') {
			return ensureLeadingSlash(slug);
		}

		return slug ? {...slug} as RouteLocationRaw : '';
	}

	function scrollBreadcrumbIntoView(event: MouseEvent, scrollToView: (el: HTMLElement) => void) {
		if (event.target instanceof HTMLElement) {
			scrollToView(event.target);
		}
	}
</script>
